import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import JsonLd from "@/components/JsonLd";
import { faqSchema } from "@/lib/seo-schemas";

export interface FaqItem {
  question: string;
  answer: string;
}

/** Convert i18n raw [{q,a}] to FaqItem[]. */
export function toFaqItems(raw: unknown): FaqItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is { q: string; a: string } => !!x && typeof x === "object" && "q" in x && "a" in x)
    .map((x) => ({ question: x.q, answer: x.a }));
}

interface FaqSectionProps {
  title: string;
  items: FaqItem[];
  className?: string;
}

/**
 * Renders a user-facing FAQ accordion AND emits FAQPage JSON-LD
 * for AEO (Google AI Overview, ChatGPT, Perplexity, etc.).
 */
const FaqSection = ({ title, items, className = "" }: FaqSectionProps) => {
  if (!items?.length) return null;

  return (
    <section className={`bg-card py-16 md:py-24 ${className}`}>
      <JsonLd data={faqSchema(items)} />
      <div className="container max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-10">
          {title}
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-base md:text-lg font-bold">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-base text-foreground/80 leading-relaxed">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FaqSection;
